import { useCallback, useEffect, useRef, useState } from 'react';

const useMeetingPip = ({ meetingManager, pipSupported }) => {
  const [pipMode, setPipMode] = useState(null);
  const [pipBusy, setPipBusy] = useState(false);

  const pipCanvasRef = useRef(null);
  const pipVideoRef = useRef(null);
  const pipStreamRef = useRef(null);
  const pipRafRef = useRef(null);
  const pipModeRef = useRef(null);
  const pipDriverRef = useRef(null);
  const pipPopoutRef = useRef(null);
  const pipOwnsStreamRef = useRef(false);
  const selectedPipVideoRef = useRef(null);

  const documentPipSupported = typeof window !== 'undefined'
    && !!window.documentPictureInPicture
    && typeof window.documentPictureInPicture.requestWindow === 'function';

  useEffect(() => {
    pipModeRef.current = pipMode;
  }, [pipMode]);

  const getTileStateForVideo = useCallback((video) => {
    if (!video || typeof video.closest !== 'function') {
      return null;
    }

    try {
      const av = meetingManager?.meetingSession?.audioVideo;
      const allTiles = av?.getAllVideoTiles?.() || [];
      for (let i = 0; i < allTiles.length; i += 1) {
        const tile = allTiles[i];
        const state = tile?.state?.() || tile?.tileState?.();
        if (!state) {
          continue;
        }

        const boundElements = [
          state.boundVideoElement,
          state.boundVideoStream?.active ? state.boundVideoElement : null,
        ].filter(Boolean);

        if (boundElements.some((el) => el === video)) {
          return state;
        }
      }
    } catch (_error) {
      // Fallback to DOM-based heuristics below.
    }

    const tile = video.closest('[data-testid="video-tile"], .video-tile, .ch-video-tile, .ch-tile');
    const blob = [
      video.className || '',
      tile?.className || '',
      video.getAttribute?.('data-testid') || '',
      tile?.getAttribute?.('data-testid') || '',
    ].join(' ');

    const tileIdMatch = blob.match(/(?:ch-remote-video--|ch-content-share--|ch-video-tile--|video-tile--)(\d+)/);
    const tileId = tileIdMatch?.[1] ? Number(tileIdMatch[1]) : null;
    if (!tileId) {
      return null;
    }

    try {
      const av = meetingManager?.meetingSession?.audioVideo;
      const videoTile = av?.getVideoTile?.(tileId);
      return videoTile?.state?.() || videoTile?.tileState?.() || null;
    } catch (_error) {
      return null;
    }
  }, [meetingManager]);

  const isLikelyContentShareVideo = useCallback((video) => {
    if (!video) {
      return false;
    }

    const tileState = getTileStateForVideo(video);
    if (tileState) {
      const attendeeId = `${tileState.boundAttendeeId || tileState.attendeeId || ''}`;
      const externalUserId = `${tileState.boundExternalUserId || tileState.externalUserId || ''}`;
      if (tileState.isContent || /#content\b/i.test(attendeeId) || /#content\b/i.test(externalUserId)) {
        return true;
      }
    }

    const tile = video.closest('[data-testid="video-tile"], .video-tile, .ch-video-tile, .ch-tile');
    const classBlob = `${tile?.className || ''} ${video.className || ''} ${tile?.getAttribute?.('data-testid') || ''} ${video.getAttribute?.('data-testid') || ''}`;
    if (/content-share|contentshare|screen-share|screen/i.test(classBlob)) {
      return true;
    }

    const srcObject = video.srcObject;
    if (srcObject && typeof srcObject.getTracks === 'function') {
      const tracks = srcObject.getTracks();
      const hasDisplayTrack = tracks.some((track) => {
        const label = `${track?.label || ''}`;
        const settings = track?.getSettings?.() || {};
        const displaySurface = `${settings.displaySurface || ''}`;
        return /screen|window|display|tab/i.test(label) || /monitor|window|browser/i.test(displaySurface);
      });
      if (hasDisplayTrack) {
        return true;
      }
    }

    return false;
  }, [getTileStateForVideo]);

  const isLikelyLocalVideo = useCallback((video) => {
    if (!video || typeof video.closest !== 'function') {
      return false;
    }

    if (video.closest('.local-camera-preview')) {
      return true;
    }

    const tileState = getTileStateForVideo(video);
    if (tileState?.localTile) {
      return true;
    }

    const tile = video.closest('[data-testid="video-tile"], .video-tile, .ch-video-tile, .ch-tile');
    const classBlob = `${tile?.className || ''} ${video.className || ''}`;
    const tileIdMatch = classBlob.match(/(?:ch-remote-video--|ch-content-share--|ch-video-tile--)?(\d+)/);
    if (!tileIdMatch?.[1]) {
      return false;
    }

    try {
      const tileId = Number(tileIdMatch[1]);
      const av = meetingManager?.meetingSession?.audioVideo;
      const videoTile = av?.getVideoTile?.(tileId);
      const tileState = videoTile?.state?.() || videoTile?.tileState?.();
      return !!tileState?.localTile;
    } catch (_error) {
      return false;
    }
  }, [getTileStateForVideo, meetingManager]);

  const getVisibleVideos = useCallback((videosContainerRef) => {
    const container = videosContainerRef.current;
    if (!container) {
      return [];
    }

    const videos = Array.from(container.querySelectorAll('video'));
    return videos.filter((video) => {
      const rect = video.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });
  }, []);

  const selectRemoteParticipantVideo = useCallback((videosContainerRef) => {
    const visible = getVisibleVideos(videosContainerRef);
    if (visible.length === 0) {
      selectedPipVideoRef.current = null;
      return null;
    }

    const remoteCandidates = visible.filter((video) => {
      if (isLikelyLocalVideo(video)) {
        return false;
      }
      if (isLikelyContentShareVideo(video)) {
        return false;
      }
      return true;
    });

    if (remoteCandidates.length === 0) {
      selectedPipVideoRef.current = null;
      return null;
    }

    const best = remoteCandidates.sort((a, b) => {
      const aRect = a.getBoundingClientRect();
      const bRect = b.getBoundingClientRect();
      return (bRect.width * bRect.height) - (aRect.width * aRect.height);
    })[0];

    selectedPipVideoRef.current = best;
    return best;
  }, [getVisibleVideos, isLikelyContentShareVideo, isLikelyLocalVideo]);

  const drawCover = (ctx, video, x, y, width, height) => {
    if (!video || video.readyState < 2) {
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(x, y, width, height);
      return;
    }

    const vW = video.videoWidth || 1;
    const vH = video.videoHeight || 1;
    const scale = Math.max(width / vW, height / vH);
    const drawW = vW * scale;
    const drawH = vH * scale;
    const drawX = x + (width - drawW) / 2;
    const drawY = y + (height - drawH) / 2;

    ctx.fillStyle = '#0f172a';
    ctx.fillRect(x, y, width, height);
    ctx.drawImage(video, drawX, drawY, drawW, drawH);
  };

  const waitForPlayableVideo = (video, timeoutMs = 1000) => new Promise((resolve) => {
    if (!video) {
      resolve(false);
      return;
    }

    if (video.readyState >= 2) {
      resolve(true);
      return;
    }

    let done = false;
    const cleanup = () => {
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('canplay', onReady);
      window.clearTimeout(timer);
    };

    const onReady = () => {
      if (done) {
        return;
      }
      done = true;
      cleanup();
      resolve(true);
    };

    const timer = window.setTimeout(() => {
      if (done) {
        return;
      }
      done = true;
      cleanup();
      resolve(video.readyState >= 2);
    }, timeoutMs);

    video.addEventListener('loadeddata', onReady);
    video.addEventListener('canplay', onReady);
  });

  const renderPipFrame = useCallback((videosContainerRef) => {
    const canvas = pipCanvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    const width = canvas.width;
    const height = canvas.height;
    const selected = selectedPipVideoRef.current;
    if (!selected || !selected.isConnected || isLikelyContentShareVideo(selected)) {
      selectRemoteParticipantVideo(videosContainerRef);
    }

    drawCover(ctx, selectedPipVideoRef.current, 0, 0, width, height);

    pipRafRef.current = window.requestAnimationFrame(() => renderPipFrame(videosContainerRef));
  }, [isLikelyContentShareVideo, selectRemoteParticipantVideo]);

  const cleanupPipResources = useCallback(() => {
    if (pipRafRef.current) {
      window.cancelAnimationFrame(pipRafRef.current);
      pipRafRef.current = null;
    }

    const pipVideo = pipVideoRef.current;
    if (pipVideo) {
      pipVideo.onleavepictureinpicture = null;
      if (pipVideo.parentNode) {
        pipVideo.parentNode.removeChild(pipVideo);
      }
      pipVideoRef.current = null;
    }

    if (pipStreamRef.current && pipOwnsStreamRef.current) {
      pipStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    pipStreamRef.current = null;
    pipOwnsStreamRef.current = false;

    const popout = pipPopoutRef.current;
    if (popout && !popout.closed) {
      popout.close();
    }
    pipPopoutRef.current = null;

    pipCanvasRef.current = null;
  }, []);

  const startClonedStreamPiP = useCallback(async (options = {}) => {
    const { immediate = false } = options;
    const selected = selectedPipVideoRef.current;
    if (!selected || typeof selected.requestPictureInPicture !== 'function') {
      return false;
    }

    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      }
    } catch (_error) {
      // Non-fatal.
    }

    cleanupPipResources();

    let stream = selected.srcObject;
    let ownsStream = false;
    if (!stream && typeof selected.captureStream === 'function') {
      stream = selected.captureStream();
      ownsStream = true;
    }
    if (!stream) {
      return false;
    }

    try {
      const pipVideo = document.createElement('video');
      pipVideo.muted = true;
      pipVideo.playsInline = true;
      pipVideo.autoplay = true;
      pipVideo.srcObject = stream;
      pipVideo.style.position = 'fixed';
      pipVideo.style.left = '-9999px';
      pipVideo.style.top = '-9999px';
      pipVideo.style.width = '1px';
      pipVideo.style.height = '1px';
      document.body.appendChild(pipVideo);

      pipVideoRef.current = pipVideo;
      pipStreamRef.current = stream;
      pipOwnsStreamRef.current = ownsStream;

      pipVideo.onleavepictureinpicture = () => {
        cleanupPipResources();
        pipModeRef.current = null;
        pipDriverRef.current = null;
        setPipMode(null);
      };

      if (!immediate) {
        await waitForPlayableVideo(pipVideo, 1500);
      }

      try {
        await pipVideo.play();
      } catch (_error) {
        // Some browsers still allow PiP even if play() rejects.
      }

      try {
        await pipVideo.requestPictureInPicture();
      } catch (_error) {
        if (!immediate) {
          throw _error;
        }

        await waitForPlayableVideo(pipVideo, 1000);
        await pipVideo.requestPictureInPicture();
      }
      pipDriverRef.current = 'stream-clone';
      return true;
    } catch (_error) {
      cleanupPipResources();
      return false;
    }
  }, [cleanupPipResources]);

  useEffect(() => {
    const onLeavePiP = () => {
      if (!document.pictureInPictureElement) {
        cleanupPipResources();
        pipModeRef.current = null;
        pipDriverRef.current = null;
        setPipMode(null);
      }
    };

    document.addEventListener('leavepictureinpicture', onLeavePiP);
    return () => {
      document.removeEventListener('leavepictureinpicture', onLeavePiP);
    };
  }, [cleanupPipResources]);

  const stopPiP = useCallback(async () => {
    setPipBusy(true);
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      }
    } catch (_error) {
      // Non-fatal.
    }

    cleanupPipResources();
    pipModeRef.current = null;
    pipDriverRef.current = null;
    setPipMode(null);
    setPipBusy(false);
  }, [cleanupPipResources]);

  const startPiP = useCallback(async (_mode, videosContainerRef) => {
    if (pipBusy) {
      return;
    }

    setPipBusy(true);
    try {
      const selected = selectRemoteParticipantVideo(videosContainerRef);
      if (!selected) {
        pipModeRef.current = null;
        pipDriverRef.current = null;
        setPipMode(null);
        return;
      }

      const mode = 'focus';
      pipModeRef.current = mode;
      setPipMode(mode);

      const clonedImmediateOk = await startClonedStreamPiP({ immediate: true });
      if (clonedImmediateOk) {
        return;
      }

      if (document.pictureInPictureElement) {
        try {
          await document.exitPictureInPicture();
        } catch (_error) {
          // Non-fatal.
        }
      }
      cleanupPipResources();

      if (!pipSupported) {
        pipModeRef.current = null;
        pipDriverRef.current = null;
        setPipMode(null);
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = 1280;
      canvas.height = 720;
      pipCanvasRef.current = canvas;

      if (typeof canvas.captureStream !== 'function') {
        throw new Error('captureStream is not supported');
      }

      const stream = canvas.captureStream(24);
      pipStreamRef.current = stream;
      pipOwnsStreamRef.current = true;

      const pipVideo = document.createElement('video');
      pipVideo.muted = true;
      pipVideo.playsInline = true;
      pipVideo.autoplay = true;
      pipVideo.srcObject = stream;
      pipVideo.style.position = 'fixed';
      pipVideo.style.left = '-9999px';
      pipVideo.style.top = '-9999px';
      pipVideo.style.width = '1px';
      pipVideo.style.height = '1px';
      document.body.appendChild(pipVideo);
      pipVideoRef.current = pipVideo;

      pipVideo.onleavepictureinpicture = () => {
        cleanupPipResources();
        pipModeRef.current = null;
        setPipMode(null);
      };

      await waitForPlayableVideo(pipVideo);
      await pipVideo.play();
      await pipVideo.requestPictureInPicture();
      pipDriverRef.current = 'canvas';
      renderPipFrame(videosContainerRef);
    } catch (_error) {
      cleanupPipResources();
      const clonedStreamOk = await startClonedStreamPiP({ immediate: false });
      if (clonedStreamOk) {
        return;
      }
      pipModeRef.current = null;
      pipDriverRef.current = null;
      setPipMode(null);
    } finally {
      setPipBusy(false);
    }
  }, [cleanupPipResources, pipBusy, pipSupported, renderPipFrame, selectRemoteParticipantVideo, startClonedStreamPiP]);

  useEffect(() => () => {
    cleanupPipResources();
  }, [cleanupPipResources]);

  const handleVideoAreaClickCapture = useCallback((event) => {
    if (!event) {
      return;
    }
  }, []);

  return {
    pipMode,
    pipBusy,
    startPiP,
    stopPiP,
    handleVideoAreaClickCapture,
    documentPipSupported,
  };
};

export default useMeetingPip;
